import { useState } from 'react'
import { Copy, Check, Pencil, FolderInput, Crop, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { withErrorCode } from '@/lib/errors'
import type { MediaDocument } from '../../services/admin.media.service'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

interface MediaDetailPanelProps {
  item: MediaDocument
  onRename: (name: string) => Promise<void>
  onMove: () => void
  onCrop: () => void
  onDelete: () => Promise<void>
}

export function MediaDetailPanel({ item, onRename, onMove, onCrop, onDelete }: MediaDetailPanelProps) {
  const [copied, setCopied] = useState(false)
  const [editing, setEditing] = useState(false)
  const [nameValue, setNameValue] = useState(item.name)
  const [deleting, setDeleting] = useState(false)

  // Sync name when item changes
  if (nameValue !== item.name && !editing) {
    setNameValue(item.name)
  }

  async function handleCopy() {
    await navigator.clipboard.writeText(item.url)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleRename() {
    const trimmed = nameValue.trim()
    if (trimmed && trimmed !== item.name) {
      await onRename(trimmed)
    }
    setEditing(false)
  }

  async function handleDelete() {
    if (!confirm(`Delete "${item.name}"? This cannot be undone.`)) return
    setDeleting(true)
    try {
      await onDelete()
    } finally {
      setDeleting(false)
    }
  }

  const statusVariant =
    item.status === 'error'
      ? 'destructive'
      : item.status === 'ready'
        ? 'default'
        : 'secondary'
  const statusLabel = item.status === 'error'
    ? withErrorCode('Upload could not be completed.', 'media/upload')
    : item.status

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Thumbnail preview */}
      {item.status === 'ready' && item.url && (
        <div className="overflow-hidden rounded-lg border bg-muted">
          <img
            src={item.url}
            alt={item.name}
            className="max-h-48 w-full object-contain"
          />
        </div>
      )}

      {/* Status badge */}
      <Badge variant={statusVariant} className="w-fit capitalize">
        {statusLabel}
      </Badge>

      {/* Name (editable) */}
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Name</Label>
        {editing ? (
          <Input
            value={nameValue}
            autoFocus
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={() => void handleRename()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleRename()
              if (e.key === 'Escape') {
                setEditing(false)
                setNameValue(item.name)
              }
            }}
            className="h-7 text-sm"
          />
        ) : (
          <button
            onClick={() => {
              setEditing(true)
              setNameValue(item.name)
            }}
            className="flex w-full items-center gap-1 rounded px-2 py-1 text-left text-sm hover:bg-muted"
          >
            <span className="flex-1 truncate">{item.name}</span>
            <Pencil className="h-3 w-3 shrink-0 text-muted-foreground" />
          </button>
        )}
      </div>

      {/* Metadata */}
      {item.status === 'ready' && (
        <div className="space-y-1 rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
          <div className="flex justify-between">
            <span>Dimensions</span>
            <span className="font-medium text-foreground">{item.width} × {item.height}</span>
          </div>
          <div className="flex justify-between">
            <span>Compressed</span>
            <span className="font-medium text-foreground">{formatBytes(item.size)}</span>
          </div>
          <div className="flex justify-between">
            <span>Original</span>
            <span>{formatBytes(item.originalSize)}</span>
          </div>
        </div>
      )}

      <Separator />

      {/* Copy URL */}
      {item.status === 'ready' && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => void handleCopy()}
          className="w-full gap-2"
        >
          {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          {copied ? 'Copied!' : 'Copy URL'}
        </Button>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-2">
        <Button variant="outline" size="sm" onClick={onMove} className="w-full gap-2">
          <FolderInput className="h-3.5 w-3.5" />
          Move to Folder
        </Button>

        {item.status === 'ready' && (
          <Button variant="outline" size="sm" onClick={onCrop} className="w-full gap-2">
            <Crop className="h-3.5 w-3.5" />
            Crop Image
          </Button>
        )}

        <Button
          variant="destructive"
          size="sm"
          onClick={() => void handleDelete()}
          disabled={deleting}
          className="w-full gap-2"
        >
          <Trash2 className="h-3.5 w-3.5" />
          {deleting ? 'Deleting…' : 'Delete'}
        </Button>
      </div>
    </div>
  )
}
